<script setup lang="ts">
import { AnimatePresence, motion } from 'motion-v';
import { computed, useAttrs } from 'vue';

import Icon from '../icon/Icon.vue';
import Typography from '../Typography/Typography.vue';

defineOptions({ inheritAttrs: false });

export type WizardContentPadding = 'none' | 'sm' | 'md' | 'lg';
export type WizardTitleVariant = 'display' | 'headline';
export type WizardProgressStyle = 'bar' | 'segments' | 'none';

export interface WizardProps {
  title?: string;
  subtitle?: string;
  titleVariant?: WizardTitleVariant;
  showBack?: boolean;
  backLabel?: string;
  currentStep?: number;
  totalSteps?: number;
  progressStyle?: WizardProgressStyle;
  contentPadding?: WizardContentPadding;
  contentClass?: string;
  headerClass?: string;
  rootClass?: string;
  centered?: boolean;
}

const props = withDefaults(defineProps<WizardProps>(), {
  titleVariant: 'headline',
  showBack: false,
  backLabel: 'Go back',
  progressStyle: 'bar',
  contentPadding: 'md',
  contentClass: '',
  headerClass: 'pb-5',
  rootClass: '',
  centered: false,
});

defineEmits<{
  back: [];
}>();

const attrs = useAttrs();

const showProgress = computed(
  () => props.progressStyle !== 'none' && props.currentStep != null && props.totalSteps != null,
);

const progressPercent = computed(() => {
  if (!showProgress.value || !props.totalSteps) {
    return 0;
  }

  if (props.totalSteps <= 1) {
    return 100;
  }

  return Math.round((props.currentStep! / props.totalSteps) * 100);
});

const paddingClass = computed(() => {
  switch (props.contentPadding) {
    case 'sm':
      return 'p-5';
    case 'lg':
      return 'p-10';
    case 'none':
      return '';
    default:
      return 'p-8';
  }
});

const hasHeader = computed(
  () => props.showBack || showProgress.value || Boolean(props.title) || Boolean(props.subtitle),
);
</script>

<template>
  <div
    :class="['h-full min-h-0 flex flex-col bg-primary relative', rootClass, attrs.class]"
    style="padding-top: var(--safe-area-top)"
  >
    <slot name="header">
      <div v-if="hasHeader" :class="['relative flex flex-col items-center px-5 pt-5', headerClass]">
        <AnimatePresence>
          <motion.button
            v-if="showBack"
            key="back"
            type="button"
            class="absolute left-5 top-5 z-10 flex h-8 w-8 items-center justify-center rounded-lg bg-white"
            :aria-label="backLabel"
            :initial="{ scale: 0 }"
            :animate="{ scale: 1 }"
            :exit="{ scale: 0 }"
            :transition="{ duration: 0.2 }"
            @click="$emit('back')"
          >
            <Icon name="arrowLeft" size="md" class="text-primary" />
          </motion.button>
        </AnimatePresence>

        <div
          class="flex w-full flex-col items-center gap-1 px-10 text-center"
          :class="showBack && ''"
        >
          <Typography v-if="title" class="max-w-xs text-white" :variant="titleVariant">
            {{ title }}
          </Typography>
          <p v-if="subtitle" class="max-w-xs text-sm text-white/80">{{ subtitle }}</p>
        </div>

        <div v-if="!showProgress" class="w-full">
          <!-- No progress -->
        </div>
        <div v-else-if="showProgress && progressStyle === 'segments'" class="mt-6 w-full">
          <p class="text-center text-sm font-medium text-white">
            Step {{ currentStep }} of {{ totalSteps }}
          </p>
          <div class="mt-2 flex gap-2">
            <div
              v-for="segment in totalSteps"
              :key="segment"
              class="h-1.5 flex-1 rounded-full transition-colors"
              :class="segment <= currentStep! ? 'bg-white' : 'bg-white/30'"
            />
          </div>
        </div>

        <div v-else-if="showProgress && progressStyle === 'bar'" class="mt-6 w-full">
          <div class="mb-1 text-center text-sm font-medium text-white">
            Step {{ currentStep }} of {{ totalSteps }}
          </div>
          <div class="h-2 w-full rounded-full bg-surface-elevated">
            <div
              class="h-2 rounded-full bg-white transition-all"
              :style="{ width: `${progressPercent}%` }"
            />
          </div>
        </div>
      </div>
    </slot>

    <slot name="title" />

    <div
      :class="[
        'flex min-h-0 flex-1 flex-col overflow-y-auto rounded-t-4xl bg-white',
        paddingClass,
        centered && 'items-center',
        contentClass,
      ]"
    >
      <slot />
    </div>

    <slot name="footer" />
  </div>
</template>
